import { create } from 'zustand';
import { Report as CricketReport, CreateReportPayload } from '@/types/cricket';
import { 
  fetchReports,
  createReport as createReportAPI,
  updateReportStatus as updateReportStatusAPI
} from '@/services/supabaseService';
import { toast } from '@/components/ui/use-toast';

interface ReportStore {
  reports: CricketReport[];
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchReports: () => Promise<void>;
  createReport: (report: CreateReportPayload) => Promise<void>;
  updateReportStatus: (reportId: string, status: CricketReport['status'], downloadUrl?: string, error?: string) => Promise<void>;
}

const useReportStore = create<ReportStore>((set, get) => ({
  reports: [] as CricketReport[],
  loading: false,
  error: null,
  
  fetchReports: async () => {
    set({ loading: true, error: null });
    try {
      const reportsData = (await fetchReports() as unknown) as CricketReport[];
      set({ reports: reportsData, loading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error fetching reports:", error);
      set({ 
        error: message || "Failed to load reports", 
        loading: false 
      });
      toast({
        title: "Error",
        description: "Failed to load reports",
        variant: "destructive",
      });
    }
  },
  
  createReport: async (report) => {
    try {
      const newReport = (await createReportAPI(report) as unknown) as CricketReport;
      set((state) => ({
        reports: [newReport, ...state.reports] as CricketReport[]
      }));
      toast({
        title: "Success",
        description: "Report created successfully!",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error creating report:", error);
      toast({
        title: "Error",
        description: message || "Failed to create report",
        variant: "destructive",
      });
      throw error;
    }
  },

  updateReportStatus: async (reportId, status, downloadUrl, error) => {
    try {
      await updateReportStatusAPI(reportId, status, downloadUrl, error);
      set((state) => ({
        reports: state.reports.map((r) =>
          r.id === reportId ? { ...r, status } : r
        ),
      }));
      toast({
        title: 'Success',
        description: `Report status updated to ${status}`,
      });
    } catch (error) {
      console.error('Error updating report status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update report status',
        variant: 'destructive',
      });
      throw error;
    }
  },
}));

export default useReportStore; 